import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import type { PutRoutingGuideType } from "~/shared/schemas/routing-guide-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import {
	deleteAndValidateTraxAr,
	putAndValidateTraxAr,
} from "~/shared/services/api/fetch-client.ts";

const putExcludeGuide = async (
	routingGuide: PutRoutingGuideType,
	customerId: number | undefined,
	isExcluded: boolean,
) => {
	const baseUrl = isExcluded
		? apiRoutes.DELETEEXCLUDEGUIDE
		: apiRoutes.PUTEXCLUDEGUIDE;

	const url = baseUrl
		.replace(":id", routingGuide.id.toString())
		.replace(":customerId", customerId?.toString() ?? "");
	const response = isExcluded
		? await deleteAndValidateTraxAr(url, z.unknown())
		: await putAndValidateTraxAr(url, null, z.unknown());
	if (!response.success) {
		throw new Error(
			`Failed to create routing guide: ${response.error}`,
			response.details instanceof Error
				? { cause: response.details }
				: undefined,
		);
	}
	return response.data;
};

export const useUpdateExcludeGuide = () => {
	const queryClient = useQueryClient();
	const { showError, showSuccess } = useNotificationActions();

	const { mutate: updateExcludeRoutingGuide } = useMutation({
		mutationFn: ({
			routingGuide,
			customerId,
			isExcluded,
		}: {
			routingGuide: PutRoutingGuideType;
			customerId: number | undefined;
			isExcluded: boolean;
		}) => putExcludeGuide(routingGuide, customerId, isExcluded),

		onSuccess: () => {
			showSuccess("Routing Guide Updated");
			void queryClient.invalidateQueries({ queryKey: ["globalRoutingGuides"] });
			void queryClient.invalidateQueries({
				queryKey: ["customerRoutingGuides"],
			});
		},

		onError: (_err) => {
			showError(`${_err}`);
		},
	});

	return { updateExcludeRoutingGuide };
};
